import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Input, List, Segmented, Spin } from "antd";
import { CheckOutlined, SearchOutlined } from "@ant-design/icons";
import ResponsiveModal from "@components/ResponsiveModal";
import globalMessage from "@/utils/globalMessage";
import { formatEquipmentRef, formatWorkorderRef } from "@/utils/entity-ref";
import {
  getEntityFilterTitle,
  getEquipmentDisplay,
  getWorkorderDisplay,
  normalizeEntityKeyword,
  FILE_MANAGEMENT_POPUP_Z_INDEX,
} from "./utils";
import "./EntityFilter.css";

const TYPE_OPTIONS = [
  { label: "Equipment", value: "equipment" },
  { label: "Workorder", value: "workorder" },
];

const PLACEHOLDER = {
  equipment: "Search equipment name...",
  workorder: "Search workorder issue or number...",
};

const WORKORDER_LIMIT = 1000;

/**
 * 按 equipment / workorder 过滤文件的选择面板（PC 弹窗与 H5 筛选抽屉共用）。
 *
 * 列表主标题展示人可读字段（equipment.name / workorder.issue），编号
 * EQ-{id} / WO-{id} 只作为灰色副标题。
 *
 * 选中实体后折算成一组目录标题关键字：
 *   equipment → [EQ-{id}, WO-{id1}, WO-{id2}, ...]（该设备关联的全部未删除工单）
 *   workorder → [WO-{id}]
 * 父组件用 matchEntityFilter / collectEntityFilterNodes 在目录树里精确匹配。
 * 另附 keywordInfo（目录标题 → { primary, secondary }），供结果列表把
 * 实体目录渲染成「名称 + 灰字编号」两行；目录 title 本身保持不变。
 */
export const EntityFilterPanel = ({
  active = true,
  fileApi,
  value = null,
  onSelect,
  onClear,
  autoFocus = false,
}) => {
  const [type, setType] = useState(value?.type || "equipment");
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectingId, setSelectingId] = useState(null);
  const seqRef = useRef(0);

  // 每次重新打开时回到当前已选类型、清空关键字
  useEffect(() => {
    if (!active) return;
    setType(value?.type || "equipment");
    setKeyword("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;

    const trimmed = keyword.trim();
    const seq = (seqRef.current += 1);
    setLoading(true);

    // 防抖 300ms（与文件搜索一致）；空关键字立即预加载
    const timer = setTimeout(async () => {
      try {
        let rows = [];
        if (type === "equipment") {
          if (typeof fileApi?.lookupEquipments === "function") {
            const res = await fileApi.lookupEquipments(encodeURIComponent(trimmed));
            rows = Array.isArray(res?.data) ? res.data : [];
          }
        } else if (typeof fileApi?.lookupWorkorders === "function") {
          const res = await fileApi.lookupWorkorders({ keyword: trimmed, limit: 50 });
          rows = Array.isArray(res?.data) ? res.data : [];
        }
        if (seq !== seqRef.current) return; // 丢弃过期响应
        setResults(rows);
      } catch (error) {
        if (seq !== seqRef.current) return;
        setResults([]);
        globalMessage.error("Failed to load options. Please try again.");
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, trimmed ? 300 : 0);

    return () => clearTimeout(timer);
  }, [active, type, keyword, fileApi]);

  const handleTypeChange = useCallback((next) => {
    setType(next);
    setKeyword("");
    setResults([]);
  }, []);

  const handlePick = useCallback(
    async (item) => {
      if (selectingId !== null) return;
      setSelectingId(item.id);
      try {
        if (type === "equipment") {
          const label = formatEquipmentRef(item.id);
          const own = getEquipmentDisplay(item);
          const res =
            typeof fileApi?.lookupWorkorders === "function"
              ? await fileApi.lookupWorkorders({
                  equipment: item.id,
                  limit: WORKORDER_LIMIT,
                })
              : null;
          const rows = Array.isArray(res?.data) ? res.data : [];
          if (rows.length >= WORKORDER_LIMIT) {
            globalMessage.warning(
              `Too many work orders; only the first ${WORKORDER_LIMIT} are included.`
            );
          }
          // 目录标题是 WO-{工单主键}，必须用 id，不能用 workordernumber
          const keywords = [label];
          const keywordInfo = { [normalizeEntityKeyword(label)]: own };
          rows.forEach((row) => {
            const ref = formatWorkorderRef(row.id);
            keywords.push(ref);
            keywordInfo[normalizeEntityKeyword(ref)] = getWorkorderDisplay(row);
          });
          onSelect?.({
            type,
            id: item.id,
            label,
            name: own.primary,
            displayName: own.primary,
            keywords: Array.from(new Set(keywords)),
            keywordInfo,
          });
        } else {
          const label = formatWorkorderRef(item.id);
          const own = getWorkorderDisplay(item);
          onSelect?.({
            type,
            id: item.id,
            label,
            name: own.primary,
            displayName: own.primary,
            keywords: [label],
            keywordInfo: { [normalizeEntityKeyword(label)]: own },
          });
        }
      } catch (error) {
        globalMessage.error("Failed to apply the filter. Please try again.");
      } finally {
        setSelectingId(null);
      }
    },
    [fileApi, onSelect, selectingId, type]
  );

  const emptyText = useMemo(
    () => (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={keyword.trim() ? "No matches found." : "No records."}
      />
    ),
    [keyword]
  );

  return (
    <div className="entity-filter-panel">
      <Segmented
        block
        options={TYPE_OPTIONS}
        value={type}
        onChange={handleTypeChange}
      />

      {type === "equipment" && (
        <div className="entity-filter-hint">
          Includes every work order folder linked to the selected equipment.
        </div>
      )}

      <Input
        allowClear
        autoFocus={autoFocus}
        prefix={<SearchOutlined />}
        placeholder={PLACEHOLDER[type]}
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
      />

      <div className="entity-filter-results">
        <List
          size="small"
          loading={loading}
          dataSource={results}
          locale={{ emptyText }}
          renderItem={(item) => {
            // 主标题用人可读字段（设备名 / 工单 issue），编号降为副标题
            const display =
              type === "equipment"
                ? getEquipmentDisplay(item)
                : getWorkorderDisplay(item);
            const isActive =
              !!value &&
              value.type === type &&
              String(value.id) === String(item.id);

            return (
              <List.Item
                className={`entity-filter-item${isActive ? " is-active" : ""}`}
                onClick={() => handlePick(item)}
              >
                <div className="entity-filter-item-main">
                  <span
                    className="entity-filter-item-label"
                    title={display.primary}
                  >
                    {display.primary}
                  </span>
                  {display.secondary ? (
                    <span className="entity-filter-item-sub">
                      {display.secondary}
                    </span>
                  ) : null}
                </div>
                {selectingId === item.id ? (
                  <Spin size="small" />
                ) : isActive ? (
                  <CheckOutlined className="entity-filter-item-check" />
                ) : null}
              </List.Item>
            );
          }}
        />
      </div>

      {value ? (
        <div className="entity-filter-footer">
          <span className="entity-filter-current">
            Filtering by <strong>{getEntityFilterTitle(value)}</strong>
          </span>
          <Button type="link" size="small" onClick={onClear}>
            Clear filter
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const EntityFilterModal = ({
  open,
  fileApi,
  value,
  onSelect,
  onClear,
  onCancel,
}) => (
  <ResponsiveModal
    open={open}
    title="Filter Files"
    width={520}
    height="78vh"
    footer={null}
    zIndex={FILE_MANAGEMENT_POPUP_Z_INDEX}
    onCancel={onCancel}
  >
    <EntityFilterPanel
      active={open}
      autoFocus
      fileApi={fileApi}
      value={value}
      onSelect={onSelect}
      onClear={onClear}
    />
  </ResponsiveModal>
);

export default EntityFilterModal;
